import type {
  CanalPublicacao,
  CategoriaDocumento,
  CategoriaPublicacao,
  MeuEstadoPublicacao,
  Publicacao,
  StatusPublicacao,
} from './publicacao.types';

/** Canal do Portal do SI — todas as listas daqui filtram por ele. */
export const CANAL_SI: CanalPublicacao = 'si';

export const CATEGORIAS_PUBLICACAO: ReadonlyArray<{
  valor: CategoriaPublicacao;
  label: string;
  /** Texto da categoria nos cards. */
  texto: string;
  /** Capa quando não há imagem. */
  capa: string;
}> = [
  { valor: 'alerta', label: 'Alerta', texto: 'text-rose-600', capa: 'from-rose-500 to-rose-600' },
  { valor: 'seguranca', label: 'Segurança da Informação', texto: 'text-primary-600', capa: 'from-primary-500 to-primary-700' },
  { valor: 'dicas', label: 'Dicas', texto: 'text-emerald-700', capa: 'from-emerald-500 to-emerald-600' },
  { valor: 'comunicados', label: 'Comunicados', texto: 'text-sky-700', capa: 'from-sky-500 to-sky-600' },
  { valor: 'politicas', label: 'Políticas', texto: 'text-violet-700', capa: 'from-violet-500 to-violet-600' },
];

export const CATEGORIAS_DOCUMENTO: ReadonlyArray<{
  valor: CategoriaDocumento;
  label: string;
  texto: string;
  icone: string;
  fundo: string;
  capa: string;
}> = [
  { valor: 'politicas', label: 'Políticas', texto: 'text-rose-600', icone: '📄', fundo: 'bg-rose-50', capa: 'from-rose-500 to-rose-600' },
  { valor: 'normas', label: 'Normas e procedimentos', texto: 'text-sky-700', icone: '🧭', fundo: 'bg-sky-50', capa: 'from-sky-500 to-sky-600' },
  { valor: 'manuais', label: 'Manuais e guias', texto: 'text-emerald-700', icone: '📘', fundo: 'bg-emerald-50', capa: 'from-emerald-500 to-emerald-600' },
  // Material de boas-vindas de SI (apresentação usada com quem está chegando).
  { valor: 'onboarding', label: 'Onboarding', texto: 'text-amber-700', icone: '🚀', fundo: 'bg-amber-50', capa: 'from-amber-500 to-orange-500' },
  { valor: 'termos', label: 'Termos', texto: 'text-violet-700', icone: '✍️', fundo: 'bg-violet-50', capa: 'from-violet-500 to-violet-600' },
  { valor: 'outros', label: 'Outros', texto: 'text-raro-gray-600', icone: '📎', fundo: 'bg-raro-gray-100', capa: 'from-slate-500 to-slate-600' },
];

export function categoriaDocumento(valor: string) {
  return CATEGORIAS_DOCUMENTO.find((c) => c.valor === valor) ?? CATEGORIAS_DOCUMENTO[CATEGORIAS_DOCUMENTO.length - 1];
}

/** Categoria do post. Recebe string porque Publicacao.categoria cobre posts e documentos. */
export function categoriaPublicacao(valor: string) {
  return CATEGORIAS_PUBLICACAO.find((c) => c.valor === valor) ?? CATEGORIAS_PUBLICACAO[0];
}

/** Rótulo/cor da categoria conforme o tipo (post ou documento). */
export function categoriaDe(publicacao: Pick<Publicacao, 'tipo' | 'categoria'>): { label: string; texto: string; capa: string } {
  return publicacao.tipo === 'documento' ? categoriaDocumento(publicacao.categoria) : categoriaPublicacao(publicacao.categoria);
}

/** O que aparece no feed da Início: posts e documentos anunciados. */
export function noFeed(publicacao: Publicacao): boolean {
  return publicacao.tipo !== 'documento' || publicacao.anunciarNoInicio === true;
}

/** Data que ordena o feed (documento com versão nova volta pro topo). */
export function dataDoFeed(publicacao: Publicacao): number {
  return millis(publicacao.anunciadoEm) ?? millis(publicacao.publicarEm) ?? 0;
}

export const STATUS_PUBLICACAO_LABELS: Record<StatusPublicacao, string> = {
  rascunho: 'Rascunho',
  agendada: 'Agendada',
  publicada: 'Publicada',
  despublicada: 'Despublicada',
};

export const STATUS_PUBLICACAO_BADGE: Record<StatusPublicacao, string> = {
  rascunho: 'bg-raro-gray-100 text-raro-gray-600',
  agendada: 'bg-blue-100 text-blue-800',
  publicada: 'bg-green-100 text-green-800',
  despublicada: 'bg-amber-100 text-amber-800',
};

/** A function de agendadas (publicarPublicacoesAgendadasSi) confere a cada 5 minutos. */
export const JANELA_AGENDAMENTO_MIN = 5;

export const AVISO_AGENDAMENTO = `Publicações agendadas podem levar até ${JANELA_AGENDAMENTO_MIN} minutos para aparecer depois do horário escolhido — o servidor confere os agendamentos a cada ${JANELA_AGENDAMENTO_MIN} minutos.`;

/** "23/10/2026 às 13:05" */
export function dataHoraAgendamento(quando: Date): string {
  return `${quando.toLocaleDateString('pt-BR')} às ${quando.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
}

/** Feed da Início: 6 por vez ("Mostrar mais publicações" traz mais 6). */
export const PUBLICACOES_POR_PAGINA = 6;
/** Fixadas ao mesmo tempo — acima disso a tela pede pra desafixar uma antes. */
export const MAXIMO_FIXADAS = 3;
export const MAIS_LIDAS_QUANTIDADE = 5;

type ComMillis = { toMillis: () => number } | null | undefined;
export function millis(valor: ComMillis): number | null {
  return valor && typeof valor.toMillis === 'function' ? valor.toMillis() : null;
}

/** Fixada e ainda dentro da data limite (se houver). */
export function estaFixada(publicacao: Publicacao, agora: number): boolean {
  if (!publicacao.fixada?.ativa) return false;
  const ate = millis(publicacao.fixada.ate);
  // Data limite vale o dia inteiro.
  return ate == null || agora <= ate + 24 * 60 * 60 * 1000 - 1;
}

/** Fixadas primeiro (a fixada mais recente antes), depois as mais novas. */
export function ordenarFeed(publicacoes: Publicacao[], agora: number): Publicacao[] {
  return [...publicacoes].sort((a, b) => {
    const fa = estaFixada(a, agora);
    const fb = estaFixada(b, agora);
    if (fa !== fb) return fa ? -1 : 1;
    return dataDoFeed(b) - dataDoFeed(a);
  });
}

export function maisLidas(publicacoes: Publicacao[], quantidade = MAIS_LIDAS_QUANTIDADE): Publicacao[] {
  return [...publicacoes]
    .filter((p) => (p.leiturasTotal ?? 0) > 0)
    .sort((a, b) => (b.leiturasTotal ?? 0) - (a.leiturasTotal ?? 0))
    .slice(0, quantidade);
}

/** A ciência desta publicação é cobrada de quem tem este colaboradorId? */
export function cienciaCobradaDe(publicacao: Publicacao, colaboradorId: string | null | undefined): boolean {
  if (!publicacao.ciencia?.exige) return false;
  if (publicacao.ciencia.publico === 'todos') return true;
  return Boolean(colaboradorId) && publicacao.ciencia.alvoIds.includes(colaboradorId as string);
}

export function cienciaEmDia(publicacao: Publicacao, estado: MeuEstadoPublicacao | undefined): boolean {
  return estado?.cienciaVersao != null && estado.cienciaVersao >= (publicacao.ciencia?.versao ?? 1);
}

export type SituacaoPrazo = { tipo: 'atrasada'; desde: number } | { tipo: 'vence'; em: number; dias: number } | { tipo: 'sem_prazo' };

const DIA = 24 * 60 * 60 * 1000;

export function situacaoPrazo(publicacao: Publicacao, agora: number): SituacaoPrazo {
  const prazo = millis(publicacao.ciencia?.prazo);
  if (prazo == null) return { tipo: 'sem_prazo' };
  const fimDoDia = prazo + DIA - 1;
  if (agora > fimDoDia) return { tipo: 'atrasada', desde: prazo };
  return { tipo: 'vence', em: prazo, dias: Math.max(0, Math.ceil((fimDoDia - agora) / DIA) - 1) };
}

export function textoPrazo(situacao: SituacaoPrazo): string {
  const data = (ms: number) => new Date(ms).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  if (situacao.tipo === 'sem_prazo') return 'Sem prazo';
  if (situacao.tipo === 'atrasada') return `Atrasada desde ${data(situacao.desde)}`;
  if (situacao.dias === 0) return 'Vence hoje';
  if (situacao.dias === 1) return 'Vence amanhã';
  return `Vence em ${situacao.dias} dias`;
}

/** Pendentes de ciência da pessoa: atrasadas, depois o prazo mais próximo, depois as sem prazo
 * (mais novas primeiro). */
export function pendenciasDeCiencia(
  publicacoes: Publicacao[],
  estados: Map<string, MeuEstadoPublicacao>,
  colaboradorId: string | null | undefined,
  agora: number
): Array<{ publicacao: Publicacao; situacao: SituacaoPrazo }> {
  const peso = (s: SituacaoPrazo) => (s.tipo === 'atrasada' ? 0 : s.tipo === 'vence' ? 1 : 2);
  return publicacoes
    .filter((p) => cienciaCobradaDe(p, colaboradorId) && !cienciaEmDia(p, estados.get(p.id)))
    .map((publicacao) => ({ publicacao, situacao: situacaoPrazo(publicacao, agora) }))
    .sort((a, b) => {
      const diff = peso(a.situacao) - peso(b.situacao);
      if (diff !== 0) return diff;
      if (a.situacao.tipo === 'atrasada' && b.situacao.tipo === 'atrasada') return a.situacao.desde - b.situacao.desde;
      if (a.situacao.tipo === 'vence' && b.situacao.tipo === 'vence') return a.situacao.em - b.situacao.em;
      return (millis(b.publicacao.publicarEm) ?? 0) - (millis(a.publicacao.publicarEm) ?? 0);
    });
}

/** Resumo da faixa: "1 atrasada · 1 vence em 2 dias · 2 sem prazo". */
export function resumoPendencias(pendencias: Array<{ situacao: SituacaoPrazo }>): string {
  const atrasadas = pendencias.filter((p) => p.situacao.tipo === 'atrasada').length;
  const semPrazo = pendencias.filter((p) => p.situacao.tipo === 'sem_prazo').length;
  const comPrazo = pendencias.filter((p) => p.situacao.tipo === 'vence');
  const partes: string[] = [];
  if (atrasadas) partes.push(`${atrasadas} ${atrasadas === 1 ? 'atrasada' : 'atrasadas'}`);
  if (comPrazo.length === 1) partes.push(textoPrazo(comPrazo[0].situacao).toLowerCase());
  else if (comPrazo.length > 1) partes.push(`${comPrazo.length} com prazo`);
  if (semPrazo) partes.push(`${semPrazo} sem prazo`);
  return partes.join(' · ');
}
